import { useEffect, useState } from "react";
import { api, downloadFile } from "@/lib/api";
import Layout from "@/components/Layout";
import { Activity, AlertTriangle, Bolt, IndianRupee, Package, ScrollText, TrendingUp, Download, Boxes } from "lucide-react";
import {
  AreaChart, Area, LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid, Legend
} from "recharts";

const CHART_COLORS = ["#2563EB", "#10B981", "#F59E0B", "#EF4444", "#8B5CF6", "#14B8A6", "#F97316"];
const TOOLTIP_STYLE = { background: "#0F172A", color: "#fff", border: "none", borderRadius: 8, fontSize: 12 };
const TICK_11 = { fontSize: 11 };
const TICK_10 = { fontSize: 10 };

function KPI({ label, value, sub, icon: Icon, accent, testId }) {
  return (
    <div data-testid={testId} className={`kpi-card ${accent}`}>
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</div>
          <div className="font-heading text-3xl font-bold text-slate-900 mt-2">{value}</div>
          {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
        </div>
        {Icon && <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center text-slate-700"><Icon size={20} /></div>}
      </div>
    </div>
  );
}

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState({ start: "", end: "" });

  const fetchData = (p) => {
    const params = p.start ? { start: p.start, end: p.end || new Date().toISOString().slice(0, 10) } : {};
    return api.get("/dashboard/summary", { params }).then((res) => { setData(res.data); setLoading(false); }).catch(() => setLoading(false));
  };

  useEffect(() => { fetchData(period); /* eslint-disable-next-line */ }, [period.start, period.end]);

  const applyPreset = (days) => {
    const end = new Date().toISOString().slice(0, 10);
    const start = new Date(Date.now() - (days - 1) * 86400000).toISOString().slice(0, 10);
    setPeriod({ start, end });
  };

  if (loading || !data) {
    return (
      <Layout title="Dashboard" subtitle="Operations overview">
        <div className="text-sm text-slate-500">Loading…</div>
      </Layout>
    );
  }

  const k = data.kpis;
  const stockPie = [
    { name: "Raw Material", value: data.stock_summary?.raw_material || 0 },
    { name: "Buffing WIP", value: data.stock_summary?.buffing || 0 },
    { name: "Finish Goods", value: data.stock_summary?.finish_goods || 0 },
  ];
  const operator = data.is_operator;
  // operator response has no stock_summary at all — guard charts that reference it
  const showStockPie = !operator && data.stock_summary;

  return (
    <Layout title="Dashboard" subtitle="Real-time operations overview">
      <div data-testid="dashboard-page" className="space-y-6">
        {!operator && (
          <div className="section-card flex flex-wrap items-center gap-3" data-testid="dashboard-period-filter">
            <span className="text-sm font-semibold text-slate-700">Period:</span>
            <div className="flex items-center gap-1.5 bg-slate-50 border border-slate-200 rounded-lg px-2 py-1">
              <input data-testid="dashboard-period-start" type="date" max={new Date().toISOString().slice(0, 10)} value={data.period?.start || ""}
                onChange={(e) => {
                  const start = e.target.value; const end = period.end || new Date().toISOString().slice(0, 10);
                  if ((new Date(end) - new Date(start)) / 86400000 > 731) { alert("Period cannot exceed 2 years."); return; }
                  setPeriod({ start, end });
                }}
                className="text-sm bg-transparent border-0 outline-none w-32" />
              <span className="text-slate-400 text-xs">→</span>
              <input data-testid="dashboard-period-end" type="date" max={new Date().toISOString().slice(0, 10)} value={data.period?.end || ""}
                onChange={(e) => {
                  const end = e.target.value; const start = period.start || end;
                  if ((new Date(end) - new Date(start)) / 86400000 > 731) { alert("Period cannot exceed 2 years."); return; }
                  setPeriod({ start, end });
                }}
                className="text-sm bg-transparent border-0 outline-none w-32" />
            </div>
            <button data-testid="dashboard-preset-7" onClick={() => applyPreset(7)} className="text-xs font-medium px-2.5 py-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700">7d</button>
            <button data-testid="dashboard-preset-30" onClick={() => applyPreset(30)} className="text-xs font-medium px-2.5 py-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700">30d</button>
            <button data-testid="dashboard-preset-90" onClick={() => applyPreset(90)} className="text-xs font-medium px-2.5 py-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700">90d</button>
            <button data-testid="dashboard-preset-365" onClick={() => applyPreset(365)} className="text-xs font-medium px-2.5 py-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700">1y</button>
            {data.period?.active && <button data-testid="dashboard-period-reset" onClick={() => setPeriod({ start: "", end: "" })} className="text-xs font-medium px-2.5 py-1.5 rounded-lg bg-blue-50 hover:bg-blue-100 text-blue-700">Reset to live (default)</button>}
            <button data-testid="dashboard-download-btn" onClick={() => downloadFile(`/export/dashboard?start=${data.period?.start || ""}&end=${data.period?.end || ""}`, `Dashboard_Summary_${data.period?.start}_to_${data.period?.end}.xlsx`)} className="text-xs font-medium px-2.5 py-1.5 rounded-lg bg-slate-900 hover:bg-slate-800 text-white flex items-center gap-1 ml-auto"><Download size={13} /> Download Summary</button>
          </div>
        )}
        {/* KPIs */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <KPI testId="kpi-today-production" label="Today's Production" value={k.today_production.toLocaleString()} sub={operator ? "Today only" : `MTD: ${k.mtd_production.toLocaleString()} pcs`} icon={Activity} accent="kpi-accent" />
          <KPI testId="kpi-today-rejection" label="Today's Rejection %" value={`${k.today_rejection_pct}%`} sub={`${k.today_rejection} pcs rejected`} icon={AlertTriangle} accent="kpi-danger" />
          <KPI testId="kpi-today-power" label="Power Today" value={`${k.today_power_units.toLocaleString()} units`} sub={operator ? "Today only" : `MTD: ${k.mtd_power_units.toLocaleString()} units`} icon={Bolt} accent="kpi-warning" />
          {!operator && <KPI testId="kpi-month-cost" label="Power Cost (MTD)" value={`₹ ${k.mtd_power_cost.toLocaleString()}`} sub={`This month`} icon={IndianRupee} accent="kpi-success" />}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {data.stock_summary && (
            <>
              <KPI testId="kpi-rm" label="Raw Material Stock" value={(data.stock_summary.raw_material || 0).toLocaleString()} icon={Package} accent="kpi-info" />
              <KPI testId="kpi-buffing" label="Buffing WIP" value={(data.stock_summary.buffing || 0).toLocaleString()} icon={Package} accent="kpi-info" />
              <KPI testId="kpi-fg" label="Finish Goods" value={(data.stock_summary.finish_goods || 0).toLocaleString()} icon={Package} accent="kpi-success" />
            </>
          )}
          <KPI testId="kpi-wip" label="Semi-Finished / WIP" value={(k.wip_qty || 0).toLocaleString()} sub="pcs between processes (e.g. buffed, not packed)" icon={Boxes} accent="kpi-info" />
          <KPI testId="kpi-gp" label="Inward / Outward Today" value={k.today_inout || 0} icon={ScrollText} accent="kpi-accent" />
        </div>

        {/* Process-wise good qty + rejection today (only final product counts toward Today's Production above) */}
        <div className="section-card" data-testid="today-process-breakdown">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading text-lg font-semibold text-slate-900">Process-wise Good Qty &amp; Rejection · Today</h3>
            <span className="text-xs text-slate-400">Today's Production counts finished goods (post-packing) only</span>
          </div>
          {(!data.today_process_breakdown || data.today_process_breakdown.length === 0) ? (
            <div className="text-sm text-slate-400 py-6 text-center">No production entries today yet.</div>
          ) : (
            <div style={{ height: Math.max(260, data.today_process_breakdown.length * 46) }}>
              <ResponsiveContainer>
                <BarChart data={data.today_process_breakdown} layout="vertical" margin={{ left: 20 }} barGap={2}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis type="number" tick={TICK_11} />
                  <YAxis type="category" dataKey="process" tick={TICK_10} width={150} interval={0} />
                  <Tooltip contentStyle={TOOLTIP_STYLE} />
                  <Legend />
                  <Bar dataKey="good_qty" name="Good Qty" fill="#10B981" radius={[0, 6, 6, 0]} />
                  <Bar dataKey="rejected_qty" name="Rejection" fill="#EF4444" radius={[0, 6, 6, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>

        {/* Charts */}
        {!operator && (<div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="section-card lg:col-span-2">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading text-lg font-semibold text-slate-900">Production Trend {data.period?.active ? `(${data.period.start} → ${data.period.end})` : "(14d)"}</h3>
              <TrendingUp size={18} className="text-blue-600" />
            </div>
            <div className="h-72">
              <ResponsiveContainer>
                <LineChart data={data.production_trend}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="date" tick={TICK_11} />
                  <YAxis tick={TICK_11} />
                  <Tooltip contentStyle={TOOLTIP_STYLE} />
                  <Legend />
                  <Line type="monotone" dataKey="production" stroke="#2563EB" strokeWidth={2.5} dot={{ r: 3 }} />
                  <Line type="monotone" dataKey="rejection" stroke="#EF4444" strokeWidth={2} dot={{ r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="section-card">
            <h3 className="font-heading text-lg font-semibold text-slate-900 mb-4">Stock Mix</h3>
            <div className="h-72">
              <ResponsiveContainer>
                <PieChart>
                  <Pie data={stockPie} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} label>
                    {stockPie.map((entry) => <Cell key={entry.name} fill={CHART_COLORS[stockPie.indexOf(entry) % CHART_COLORS.length]} />)}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>)}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {!operator && (
          <div className="section-card">
            <h3 className="font-heading text-lg font-semibold text-slate-900 mb-4">Power Consumption {data.period?.active ? `(${data.period.start} → ${data.period.end})` : "(30d)"}</h3>
            <div className="h-72">
              <ResponsiveContainer>
                <AreaChart data={data.power_trend}>
                  <defs>
                    <linearGradient id="pwr" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#F59E0B" stopOpacity={0.6} />
                      <stop offset="100%" stopColor="#F59E0B" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="date" tick={TICK_11} />
                  <YAxis tick={TICK_11} />
                  <Tooltip contentStyle={TOOLTIP_STYLE} />
                  <Area type="monotone" dataKey="units" stroke="#F59E0B" fill="url(#pwr)" strokeWidth={2.5} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>)}

          <div className="section-card">
            <h3 className="font-heading text-lg font-semibold text-slate-900 mb-4">Rejection by Stage {data.period?.active ? `(${data.period.start} → ${data.period.end})` : "(30d)"}</h3>
            <div className="h-72">
              <ResponsiveContainer>
                <BarChart data={data.stage_breakdown}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="stage" tick={TICK_10} angle={-25} textAnchor="end" height={60} interval={0} />
                  <YAxis tick={TICK_11} />
                  <Tooltip contentStyle={TOOLTIP_STYLE} />
                  <Bar dataKey="rejection_pct" fill="#EF4444" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
